// S4.3 (T13): cobertura prepago del cliente derivada de los pagos ADELANTADO
// que el detalle del cliente ya consulta (GET /admin/clientes/:id/pagos).
//
// El pago de un paquete prepago guarda la ventana completa del paquete en
// pago_cliente.fecha_inicio/fecha_fin (el backend la escribe con el primer y
// el último período del paquete). Los adelantos legacy, además, exponen el
// rango realmente aplicado a facturas (periodoCubiertoInicio/Fin). No hay un
// endpoint de paquetes en el detalle, así que esta es la única fuente de
// cobertura disponible allí.
import dayjs, { type Dayjs } from 'dayjs';
import type { ClientePrepagoCobertura, DateOnlyString, PagoClienteResponse } from '@aid/shared';
import { parseDateOnly } from '../../lib/date';
import { rangoCubierto } from '../pagos/lib/pago-table-helpers';

export interface CoberturaPrepago {
  /** Pago ADELANTADO de origen; null cuando la ventana viene del paquete. */
  idPago: number | null;
  /** Paquete de origen (vía detalle); ausente en la vía de pagos. */
  idPaquete?: number;
  /** Date-only (DateOnlyString contract) — nunca instantes UTC-midnight. */
  inicio: DateOnlyString;
  fin: DateOnlyString;
  /** Meses adelantados declarados por el pago, si el backend los expone. */
  meses: number | null;
  /** true cuando hoy cae dentro de la ventana [inicio, fin] (inclusive). */
  vigente: boolean;
  /** true cuando la ventana comienza después de hoy. */
  futura: boolean;
}

/**
 * Cobertura prepago viva (vigente o futura) más relevante del cliente: la
 * ventana que cubre hoy; si ninguna lo hace, la próxima más cercana. La
 * cobertura vencida es historial y no se muestra. Los pagos anulados y las
 * modalidades NORMAL no son cobertura de paquete.
 *
 * S6.3: el desempate del paquete que gobierna es uno solo, compartido con el
 * backend (`findCoberturaPrepagoRaw` y `buscarPaqueteActivoCubriendoPeriodo`):
 * entre ventanas igual de elegibles gana inicio ASC, después fin ASC y
 * después id ASC. Antes esta vía prefería la ventana vigente iniciada más
 * recientemente y podía describir un paquete distinto al que gobierna.
 */
export function coberturaPrepago(
  pagos: PagoClienteResponse[],
  hoy: Dayjs = dayjs(),
): CoberturaPrepago | null {
  const hoyDia = hoy.startOf('day');
  let vigente: CandidataCobertura | null = null;
  let futura: CandidataCobertura | null = null;

  for (const pago of pagos) {
    if (pago.modalidad !== 'ADELANTADO') continue;
    if (pago.motivoAnulacion) continue;

    const [inicioRaw, finRaw] = rangoCubierto(pago);
    if (!inicioRaw || !finRaw) continue;

    const inicio = parseDateOnly(inicioRaw);
    const fin = parseDateOnly(finRaw);
    if (!inicio || !fin) continue;
    // Un rango invertido o vencido no explica la facturación del período.
    if (fin.isBefore(inicio, 'day')) continue;
    if (fin.isBefore(hoyDia, 'day')) continue;

    const iniciaHoyOMasTarde = inicio.isAfter(hoyDia, 'day');
    const candidata: CoberturaPrepago = {
      idPago: pago.id,
      inicio: inicioRaw,
      fin: finRaw,
      meses: pago.mesesAdelantado ?? null,
      vigente: !iniciaHoyOMasTarde,
      futura: iniciaHoyOMasTarde,
    };
    const conFechas: CandidataCobertura = { candidata, inicio, fin, id: pago.id };

    if (candidata.vigente) {
      if (vigente === null || esCandidataAnterior(conFechas, vigente)) vigente = conFechas;
    } else if (futura === null || esCandidataAnterior(conFechas, futura)) {
      futura = conFechas;
    }
  }

  return vigente?.candidata ?? futura?.candidata ?? null;
}

interface CandidataCobertura {
  candidata: CoberturaPrepago;
  inicio: Dayjs;
  fin: Dayjs;
  id: number;
}

/** Desempate canónico compartido con el backend: inicio, fin, id, todos ASC. */
function esCandidataAnterior(a: CandidataCobertura, b: CandidataCobertura): boolean {
  if (!a.inicio.isSame(b.inicio, 'day')) return a.inicio.isBefore(b.inicio, 'day');
  if (!a.fin.isSame(b.fin, 'day')) return a.fin.isBefore(b.fin, 'day');
  return a.id < b.id;
}

/**
 * S6.4: resultado tipado de la cobertura visible. `PAQUETE_INVALIDO` expone
 * un snapshot presente pero ilegible (fechas rotas o rango invertido) para
 * que la UI lo muestre en vez de ocultarlo; la ventana vencida sigue siendo
 * `SIN_COBERTURA` (historial, no corrupción).
 */
export type EstadoCoberturaPrepago =
  | { tipo: 'COBERTURA'; cobertura: CoberturaPrepago }
  | { tipo: 'PAQUETE_INVALIDO'; idPaquete: number }
  | { tipo: 'SIN_COBERTURA' };

/**
 * S4.3 (T27) + S6.4: cobertura visible que combina el paquete del detalle con
 * el fallback por ventana de pago.
 *
 *  - paquete no ACTIVO (CANCELADO/CONSUMIDO/superseded) → sin cobertura,
 *    aunque su pago ADELANTADO siga VIGENTE (la mentira que T27 corrige);
 *  - paquete ACTIVO con snapshot b02e536 → ventana exacta del paquete;
 *    si el snapshot está presente pero es ilegible, se expone como inválido;
 *  - paquete ACTIVO sin snapshot o detalle sin paquete → ventana del pago.
 */
export function estadoCoberturaPrepago(
  paquete: ClientePrepagoCobertura | null | undefined,
  pagos: PagoClienteResponse[],
  hoy: Dayjs = dayjs(),
): EstadoCoberturaPrepago {
  if (!paquete) return coberturaDesdePagos(pagos, hoy);
  if (paquete.estado !== 'ACTIVO') return { tipo: 'SIN_COBERTURA' };
  if (!paquete.tieneSnapshot) return coberturaDesdePagos(pagos, hoy);

  const hoyDia = hoy.startOf('day');
  const inicio = parseDateOnly(paquete.inicio);
  const fin = parseDateOnly(paquete.fin);
  // S6.4: un snapshot presente pero ilegible no se degrada en silencio.
  if (!inicio || !fin || fin.isBefore(inicio, 'day')) {
    return { tipo: 'PAQUETE_INVALIDO', idPaquete: paquete.idPaquete };
  }
  // Una ventana vencida es historial: no es cobertura y no es corrupción.
  if (fin.isBefore(hoyDia, 'day')) return { tipo: 'SIN_COBERTURA' };

  const futura = inicio.isAfter(hoyDia, 'day');
  return {
    tipo: 'COBERTURA',
    cobertura: {
      idPago: null,
      idPaquete: paquete.idPaquete,
      inicio: paquete.inicio,
      fin: paquete.fin,
      meses: paquete.meses,
      vigente: !futura,
      futura,
    },
  };
}

function coberturaDesdePagos(pagos: PagoClienteResponse[], hoy: Dayjs): EstadoCoberturaPrepago {
  const cobertura = coberturaPrepago(pagos, hoy);
  return cobertura ? { tipo: 'COBERTURA', cobertura } : { tipo: 'SIN_COBERTURA' };
}

/** Compatibilidad: los llamadores que solo necesitan la ventana siguen viendo
 * `null` para `PAQUETE_INVALIDO` y `SIN_COBERTURA`. El banner usa
 * `estadoCoberturaPrepago` para poder distinguirlos. */
export function resolverCoberturaPrepago(
  paquete: ClientePrepagoCobertura | null | undefined,
  pagos: PagoClienteResponse[],
  hoy: Dayjs = dayjs(),
): CoberturaPrepago | null {
  const estado = estadoCoberturaPrepago(paquete, pagos, hoy);
  return estado.tipo === 'COBERTURA' ? estado.cobertura : null;
}
